/**
 * Shared responsive wizard shell.
 *
 * This is the loanDepot-styled step experience proven by the Wave 5 refinance
 * wizard — purple header with the approved local logo, light page surface, a
 * centered step card, a purple progress indicator, and deterministic Previous /
 * Continue / Skip controls — extracted by WI-TV-W6-001 so the purchase journey
 * renders from exactly the same primitives instead of a parallel implementation.
 * `renderRefinanceWizard` and `renderPurchaseWizard` are thin journey-specific
 * wrappers around it.
 *
 * Behavior contract enforced here:
 * - Continue validates the current step; on failure it renders an inline error,
 *   associates it with the first invalid control, and moves focus there.
 * - Skip is rendered only for optional steps and advances without validation,
 *   never discarding entered values.
 * - Previous retains all entered values (the machine keeps them).
 * - Progress counts input steps only and is announced to assistive tech.
 * - On the final required step the primary control submits the referral once.
 *   While the call is in flight every wizard control is disabled and the
 *   submission controller collapses repeated activation onto the single call, so
 *   one deliberate attempt is exactly one LeadIntake call.
 * - The thank-you panel is rendered only after a successful LeadIntake response.
 *   It is not a numbered step, so progress semantics are unchanged.
 * - A failed attempt keeps the borrower on the final step with a sanitized
 *   friendly message, retry guidance, and the attempt's correlation reference.
 *   Nothing is replayed automatically; only another deliberate activation
 *   submits again.
 * - Progress and navigation use the applicable steps only, so a journey whose
 *   cadence depends on an answer renumbers itself as soon as that answer
 *   changes. Prefill never advances, with one exception: when an accepted
 *   prefill removes the step being displayed, the shell relocates once to the
 *   next applicable step with deterministic heading focus, validating and
 *   submitting nothing.
 * - A step body may report that the borrower removed the optional branch it
 *   belongs to (the co-borrower tail's Remove action). The shell prunes what is
 *   now unreachable and relocates once with deterministic heading focus when the
 *   displayed step ceased to exist, validating and submitting nothing.
 * - When a `changeLoanType` action is wired, every pre-submit step exposes a
 *   visible, keyboard-accessible control that hands back to the journey owner to
 *   start over. It is refused while an attempt is in flight and once a referral
 *   has been accepted, so terminal submission semantics are preserved, and the
 *   shell itself neither submits nor emits an observer event for it.
 */
import logoUrl from "../assets/loandepot-logo.svg";
import { contain, type MaybePromise } from "../callbacks";
import {
  WizardMachine,
  inapplicableValueKeys,
  type WizardFieldError,
  type WizardStep,
} from "./wizard-machine";
import { readRefinancePrefillFromMapped } from "./refinance-prefill";
import { WIZARD_STEP_RESET_EVENT, type WizardStepResetDetail } from "./step-fields";
import { MILITARY_COMPONENT_KEY, supersedesMilitaryComponent } from "./borrower-steps";
import {
  createRefinanceSubmitter,
  type RefinanceSubmissionDeps,
  type RefinanceSubmissionOutcome,
  type RefinanceSubmitter,
} from "./refinance-submission";

const LOGO_ALT = "loanDepot";

/**
 * Applies the mapped values of an accepted prefill message to a mounted wizard
 * and returns the value keys that were actually seeded. Values the wizard would
 * not accept itself are dropped; nothing is auto-advanced or auto-submitted.
 */
export type WizardPrefillApplier = (mapped: Readonly<Record<string, string>>) => string[];

/** Tells the journey owner whether leaving this wizard is currently allowed. */
export type WizardSwitchProbe = () => boolean;

/**
 * Reads an immutable snapshot of the mounted wizard's current in-memory values.
 * The journey owner uses it to capture a switchable journey's answers for
 * partner-routed retention rather than scraping untrusted or stale DOM state.
 */
export type WizardSnapshotReader = () => Readonly<Record<string, string>>;

export interface WizardShellOptions {
  /** Step list override, primarily for tests. */
  steps: WizardStep[];
  /** Initial values (e.g. from prefill), primarily for tests. */
  initialValues?: Record<string, string>;
  /** Journey label rendered under the logo, e.g. `"Purchase"`. */
  headerLabel: string;
  /** Journey noun used in the thank-you heading, e.g. `"purchase referral"`. */
  referralNoun: string;
  /** Copy for the navigation-only completion panel (no submitter wired). */
  completion: { title: string; body: string };
  /**
   * Submission wiring. When present the final step submits the referral to LMD;
   * when absent the wizard is navigation-only (used by foundation tests).
   */
  submission?: RefinanceSubmissionDeps;
  /** Submitter override, primarily for tests that assert call counts. */
  submitter?: RefinanceSubmitter;
  /**
   * Builds the submitter from {@link submission} deps for this journey.
   * Defaults to the refinance submitter; the purchase wizard supplies the
   * purchase submitter so its cumulative answers project and validate as a
   * purchase referral through the same shared submission guarantees.
   */
  submitterFactory?: (deps: RefinanceSubmissionDeps) => RefinanceSubmitter;
  /**
   * Called once the wizard is closed out: after a successful submission when a
   * submitter is wired, or immediately on completing the final step when it is
   * navigation-only.
   */
  onComplete?: (values: Readonly<Record<string, string>>) => MaybePromise<void>;
  /** Observes each submission outcome, primarily for tests. */
  onSubmitOutcome?: (outcome: RefinanceSubmissionOutcome) => MaybePromise<void>;
  /**
   * Receives the wizard's prefill applier once the wizard is mounted, so the
   * caller can bridge inbound `TURBOVETS_PREFILL` messages into the wizard's
   * values on the entry paths where no legacy form is mounted to apply them to.
   */
  onPrefillBridge?: (apply: WizardPrefillApplier) => void;
  /**
   * Receives a reader for the mounted wizard's current in-memory values once it
   * is mounted, so the journey owner can capture a switchable journey's answers
   * for partner-routed retention without scraping the DOM.
   */
  onSnapshotBridge?: (readSnapshot: WizardSnapshotReader) => void;
  /**
   * Sanitizer for bridged prefill values. Defaults to the refinance rules. It
   * receives the wizard's current answers as well, so a journey whose contract
   * makes a value's admissibility depend on another answer can enforce that
   * rule on the prefill path too, not only on the interaction path.
   */
  sanitizePrefill?: (
    mapped: Readonly<Record<string, string>>,
    current: Readonly<Record<string, string>>,
  ) => Record<string, string>;
  /**
   * Wires the shared pre-submit "Change loan type" affordance. `onActivate` is
   * invoked only when switching is allowed; the journey owner performs the
   * destructive reset.
   */
  changeLoanType?: {
    onActivate: () => void;
    /** Receives a probe reporting whether switching is currently allowed. */
    onProbe?: (canSwitch: WizardSwitchProbe) => void;
  };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Renders the wizard into `root` and returns the {@link WizardMachine} driving
 * it so callers/tests can inspect navigation state.
 */
export function renderWizardShell(
  root: HTMLElement,
  options: WizardShellOptions,
): WizardMachine {
  const machine = new WizardMachine(options.steps, options.initialValues ?? {});
  const submitter =
    options.submitter ??
    (options.submission
      ? (options.submitterFactory ?? createRefinanceSubmitter)(options.submission)
      : undefined);
  const sanitizePrefill = options.sanitizePrefill ?? readRefinancePrefillFromMapped;
  const switchable = options.changeLoanType !== undefined;

  root.innerHTML = `
    <div class="wizard" data-wizard>
      <header class="wizard-header">
        <img class="wizard-logo" src="${logoUrl}" alt="${LOGO_ALT}" width="172" height="34" />
        <p class="wizard-header-sub">${escapeHtml(options.headerLabel)}</p>
      </header>
      <main class="wizard-surface">
        <div class="wizard-card" role="group" aria-labelledby="wizard-step-title">
          <div class="wizard-progress">
            <p class="wizard-progress-label" id="wizard-progress-label" aria-live="polite"></p>
            <div
              class="wizard-progress-track"
              role="progressbar"
              aria-labelledby="wizard-progress-label"
              aria-valuemin="1"
            >
              <span class="wizard-progress-fill" data-progress-fill></span>
            </div>
          </div>
          <h1 class="wizard-step-title" id="wizard-step-title" tabindex="-1"></h1>
          <div class="wizard-error" data-error hidden role="alert"></div>
          <div class="wizard-step-body" data-step-body></div>
          <div class="wizard-status" data-status hidden role="status" aria-live="polite"></div>
          <div class="wizard-submit-error" data-submit-error hidden role="alert" tabindex="-1"></div>
          <div class="wizard-actions">
            <button type="button" class="wizard-btn wizard-btn-secondary" data-back>
              Previous
            </button>
            <span class="wizard-actions-spacer"></span>
            <button type="button" class="wizard-btn wizard-btn-ghost" data-skip hidden>
              Skip
            </button>
            <button type="button" class="wizard-btn wizard-btn-primary" data-continue>
              Continue
            </button>
          </div>
          ${
            switchable
              ? `<div class="wizard-switch" data-switch>
            <button
              type="button"
              class="wizard-btn wizard-btn-link"
              data-change-loan-type
              aria-describedby="wizard-switch-note"
            >
              Change loan type
            </button>
            <p class="wizard-switch-note" id="wizard-switch-note">
              Changing your loan type starts your application over. Your answers so far will be
              cleared.
            </p>
          </div>`
              : ""
          }
        </div>
      </main>
    </div>`;

  const el = <T extends HTMLElement>(selector: string): T => {
    const found = root.querySelector<T>(selector);
    if (!found) throw new Error(`Wizard element not found: ${selector}`);
    return found;
  };

  const titleEl = el<HTMLHeadingElement>("#wizard-step-title");
  const bodyEl = el<HTMLDivElement>("[data-step-body]");
  const errorEl = el<HTMLDivElement>("[data-error]");
  const progressLabel = el<HTMLParagraphElement>("#wizard-progress-label");
  const progressTrack = el<HTMLDivElement>(".wizard-progress-track");
  const progressFill = el<HTMLSpanElement>("[data-progress-fill]");
  const backBtn = el<HTMLButtonElement>("[data-back]");
  const skipBtn = el<HTMLButtonElement>("[data-skip]");
  const continueBtn = el<HTMLButtonElement>("[data-continue]");
  const statusEl = el<HTMLDivElement>("[data-status]");
  const submitErrorEl = el<HTMLDivElement>("[data-submit-error]");
  const switchBtn = root.querySelector<HTMLButtonElement>("[data-change-loan-type]");

  const completionEl = document.createElement("div");

  /**
   * Sticky flag for a non-retryable submission failure. It outranks the live
   * validity rule so a validity listener can never re-enable the primary action
   * after a failure that resubmitting cannot fix; it is cleared whenever a fresh
   * step is rendered.
   */
  let nonRetryableFailure = false;

  /** True when the current step has no visible required/malformed-entry error. */
  function currentStepValid(): boolean {
    return machine.validateCurrent().length === 0;
  }

  /**
   * Recomputes the primary-action label and disabled state deterministically.
   * Precedence, highest first: a terminal success has already replaced the card
   * (nothing to do); an in-flight attempt always disables it; a non-retryable
   * failure always disables it; a retryable/unexpected failure exposes an enabled
   * `Try again` only when the current step validates; otherwise the normal state
   * follows `validateCurrent`. Busy, terminal, and non-retryable states are never
   * re-enabled by a validity listener.
   */
  function syncPrimaryAction(): void {
    if (machine.isComplete) return;
    continueBtn.textContent = finalActionLabel();
    if (submitter?.inFlight === true || nonRetryableFailure) {
      continueBtn.disabled = true;
      return;
    }
    continueBtn.disabled = !currentStepValid();
  }

  /**
   * Switching loan type is a pre-submit affordance only: an attempt that is in
   * flight owns the answers being sent, and an accepted referral is terminal, so
   * neither may be discarded out from under LMD.
   */
  function canSwitchLoanType(): boolean {
    if (!switchable) return false;
    if (machine.isComplete) return false;
    return submitter?.inFlight !== true && submitter?.completed !== true;
  }

  const GENERATED_ERROR_ID = "wizard-error-message";

  /**
   * Removes the generated invalid/error association from a single control while
   * preserving any genuine, pre-existing `aria-describedby` tokens. Restores the
   * description recorded in `data-describedby-base` (which never contains the
   * generated token), or strips `aria-describedby` entirely when there was no
   * genuine base. Idempotent and safe on a control that was never marked.
   */
  function clearControlErrorState(control: HTMLElement): void {
    control.removeAttribute("aria-invalid");
    if (control.hasAttribute("data-describedby-base")) {
      const base = control.getAttribute("data-describedby-base") ?? "";
      if (base === "") control.removeAttribute("aria-describedby");
      else control.setAttribute("aria-describedby", base);
      control.removeAttribute("data-describedby-base");
      return;
    }
    // No recorded genuine base: drop only the generated token, keeping the rest.
    const current = control.getAttribute("aria-describedby");
    if (current === null) return;
    const remaining = current.split(/\s+/).filter((t) => t !== "" && t !== GENERATED_ERROR_ID);
    if (remaining.length === 0) control.removeAttribute("aria-describedby");
    else control.setAttribute("aria-describedby", remaining.join(" "));
  }

  function clearError(): void {
    errorEl.hidden = true;
    errorEl.textContent = "";
    bodyEl
      .querySelectorAll<HTMLElement>("[aria-invalid='true'], [data-describedby-base]")
      .forEach((c) => clearControlErrorState(c));
  }

  /**
   * Renders the first error inline, associates it with its control via
   * `aria-describedby`, and (by default) moves focus to that control. Live
   * guidance passes `moveFocus: false` so an error surfaced while the borrower
   * is still editing a field is announced and reachable without stealing focus.
   *
   * Before associating the new control it clears the generated state from every
   * other control, so moving guidance from one field to another never leaves a
   * stale `aria-invalid` or a dangling association behind. Re-associating the
   * same control is idempotent: the genuine base is recorded once and the
   * generated token is never duplicated.
   */
  function showError(errors: WizardFieldError[], options: { moveFocus?: boolean } = {}): void {
    if (errors.length === 0) return;
    const moveFocus = options.moveFocus !== false;
    errorEl.hidden = false;
    errorEl.textContent = errors[0].message;
    errorEl.id = GENERATED_ERROR_ID;
    const first = errors[0];
    const control = bodyEl.querySelector<HTMLElement>(
      `[id="${first.field.replace(/"/g, '\\"')}"]`,
    );
    // Any previously-marked control that is not the current target is cleared,
    // preserving its genuine descriptions.
    bodyEl
      .querySelectorAll<HTMLElement>("[aria-invalid='true'], [data-describedby-base]")
      .forEach((c) => {
        if (c !== control) clearControlErrorState(c);
      });
    if (control) {
      control.setAttribute("aria-invalid", "true");
      // Record the genuine base exactly once so repeated errors on the same
      // control neither lose it nor fold the generated token back into it.
      if (!control.hasAttribute("data-describedby-base")) {
        const existing = control.getAttribute("aria-describedby");
        control.setAttribute("data-describedby-base", existing ?? "");
      }
      const base = control.getAttribute("data-describedby-base") ?? "";
      const tokens = base.split(/\s+/).filter((t) => t !== "" && t !== GENERATED_ERROR_ID);
      tokens.push(GENERATED_ERROR_ID);
      control.setAttribute("aria-describedby", tokens.join(" "));
      if (moveFocus) control.focus();
    } else if (moveFocus) {
      errorEl.focus();
    }
  }

  /**
   * Value keys/control ids the borrower has interacted with on the current
   * step. Live validation guidance is shown only for touched controls, so an
   * untouched step is never pre-populated with errors the borrower has not yet
   * had a chance to satisfy. Cleared whenever a fresh step is rendered.
   */
  const touched = new Set<string>();

  /** True for a text-like control whose malformed entry warrants live guidance. */
  function isGuidableControl(el: EventTarget | null): el is HTMLInputElement | HTMLSelectElement {
    if (el instanceof HTMLSelectElement) return el.id !== "";
    if (el instanceof HTMLInputElement) {
      return el.id !== "" && el.type !== "radio" && el.type !== "checkbox";
    }
    return false;
  }

  /**
   * Surfaces the first current-step validation error whose control the borrower
   * has already touched, associated and announced through the same inline
   * region as submit-time errors, without moving focus. Continue stays disabled
   * on its own precedence; this only removes the disabled button as the sole
   * explanation of why. Idempotent: it re-announces only when the target control
   * or the message changes, and it clears once the touched fields validate.
   *
   * The target control identity is compared as well as the message: two touched
   * invalid controls can share the same message, so correcting the first (which
   * moves guidance to the second while the visible text is unchanged) must still
   * re-associate — otherwise the corrected control would retain a stale
   * `aria-invalid`/generated association and the second would receive none.
   */
  function syncLiveGuidance(): void {
    const errors = machine.validateCurrent();
    const first = errors.find(
      (e) =>
        touched.has(e.field) &&
        bodyEl.querySelector(`[id="${e.field.replace(/"/g, '\\"')}"]`) !== null,
    );
    if (!first) {
      if (!errorEl.hidden) clearError();
      return;
    }
    const currentTarget = bodyEl.querySelector<HTMLElement>("[aria-invalid='true']");
    const targetChanged = currentTarget === null || currentTarget.id !== first.field;
    if (errorEl.hidden || errorEl.textContent !== first.message || targetChanged) {
      showError([first], { moveFocus: false });
    }
  }

  /**
   * Refreshes the progress label, progressbar, and numbered heading for the
   * current position without re-rendering the step body. A cadence that depends
   * on an answer therefore renumbers as soon as that answer changes, while the
   * borrower keeps their caret, selection, and focus.
   */
  function syncProgress(): void {
    if (machine.isComplete) return;
    const progress = machine.progress();
    progressLabel.textContent = `Step ${progress.current} of ${progress.total}: ${machine.current.title}`;
    progressTrack.setAttribute("aria-valuenow", String(progress.current));
    progressTrack.setAttribute("aria-valuemax", String(progress.total));
    progressTrack.setAttribute("aria-valuetext", `Step ${progress.current} of ${progress.total}`);
    progressFill.style.width = `${Math.round(progress.ratio * 100)}%`;
    titleEl.textContent = `${progress.current}. ${machine.current.title}`;
  }

  function renderStep(focusTitle: boolean, focusHeading = false): void {
    if (machine.isComplete) {
      renderCompletion();
      return;
    }
    const step = machine.current;
    clearError();
    // A fresh step starts with a clean interaction slate, so no live guidance
    // carries over from the fields of the step just left.
    touched.clear();
    // Navigating away from a failed attempt clears its feedback: the failure
    // belongs to that attempt, and nothing is resubmitted on its behalf.
    clearSubmitFeedback();

    syncProgress();

    bodyEl.innerHTML = "";
    step.render(bodyEl, machine.values);

    backBtn.disabled = machine.isFirst;
    skipBtn.hidden = step.optional !== true || (submitter !== undefined && machine.isLast);
    // The controls are re-enabled for the step being rendered: a control left
    // disabled by an earlier attempt (a non-retryable failure, or an attempt in
    // flight when it was rendered) must never outlive that attempt and strand
    // the borrower with no way forward.
    skipBtn.disabled = false;
    nonRetryableFailure = false;
    if (switchBtn) switchBtn.disabled = !canSwitchLoanType();
    // Continue reflects the current step's live validity from first render, so a
    // required step with no valid answer yet cannot be continued.
    syncPrimaryAction();

    if (focusHeading) {
      // A relocation the borrower did not ask for lands on the numbered heading
      // deterministically, so the step they were moved to is announced rather
      // than silently swapping the control under their focus.
      titleEl.focus();
    } else if (focusTitle) {
      const autofocus = bodyEl.querySelector<HTMLElement>("[data-autofocus='true']");
      (autofocus ?? titleEl).focus();
    }
  }

  /** Label for the primary control: submit wording only on the final step. */
  function finalActionLabel(): string {
    if (!machine.isLast) return "Continue";
    if (!submitter) return "Review";
    return submitErrorEl.hidden ? "Submit referral" : "Try again";
  }

  function clearSubmitFeedback(): void {
    statusEl.hidden = true;
    statusEl.textContent = "";
    submitErrorEl.hidden = true;
    submitErrorEl.innerHTML = "";
  }

  /**
   * Disables every wizard control for the duration of an attempt. The in-flight
   * submission controller is the authoritative dedupe guard; this is the visible
   * half of it, and it also keeps the answers being submitted from changing
   * underneath the call.
   */
  function setInFlight(busy: boolean): void {
    const card = root.querySelector<HTMLElement>(".wizard-card");
    if (card) {
      if (busy) card.setAttribute("aria-busy", "true");
      else card.removeAttribute("aria-busy");
    }
    continueBtn.disabled = busy;
    backBtn.disabled = busy || machine.isFirst;
    skipBtn.disabled = busy;
    if (switchBtn) switchBtn.disabled = busy || !canSwitchLoanType();
    bodyEl
      .querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>("input, select, button")
      .forEach((control) => {
        control.disabled = busy;
      });
  }

  /**
   * Renders the recoverable failure state: the sanitized friendly message for
   * the error kind, retry guidance, and the attempt's correlation reference. No
   * borrower value, raw response body, or exact status text is rendered.
   */
  function renderSubmitFailure(
    outcome: Extract<RefinanceSubmissionOutcome, { status: "failed" }>,
  ): void {
    const guidance = outcome.retryable
      ? "Your answers are still here. You can try submitting again."
      : "Your answers are still here. Please contact loanDepot support for help with this referral.";
    submitErrorEl.hidden = false;
    submitErrorEl.innerHTML = `
      <h2 class="wizard-submit-error-title">Referral not submitted</h2>
      <p>${escapeHtml(outcome.message)}</p>
      <p class="wizard-retry-guidance">${escapeHtml(guidance)}</p>
      <p class="wizard-correlation">Reference: <span data-correlation>${escapeHtml(outcome.correlationId)}</span></p>`;
    // A non-retryable failure (misconfiguration, authorization) cannot be fixed
    // by resubmitting, so the primary control stays out of the way instead of
    // inviting a pointless additional call; a retryable one enables `Try again`
    // only when the current step still validates.
    nonRetryableFailure = !outcome.retryable;
    syncPrimaryAction();
    if (!outcome.retryable) skipBtn.disabled = true;
    submitErrorEl.focus();
  }

  /** Renders the accessible validation summary for unsubmittable answers. */
  function renderIncomplete(errors: readonly string[]): void {
    submitErrorEl.hidden = false;
    submitErrorEl.innerHTML = `
      <h2 class="wizard-submit-error-title">Please finish your referral</h2>
      <ul>${errors.map((e) => `<li>${escapeHtml(e)}</li>`).join("")}</ul>`;
    nonRetryableFailure = false;
    syncPrimaryAction();
    submitErrorEl.focus();
  }

  /**
   * Renders the recoverable state for an unexpected exception, which carries no
   * sanitized taxonomy of its own. The answers are kept and the borrower may
   * deliberately try again; no diagnostic detail is shown.
   */
  function renderUnexpectedFailure(): void {
    submitErrorEl.hidden = false;
    submitErrorEl.innerHTML = `
      <h2 class="wizard-submit-error-title">Referral not submitted</h2>
      <p>Something went wrong while submitting your referral.</p>
      <p class="wizard-retry-guidance">Your answers are still here. You can try submitting again.</p>`;
    nonRetryableFailure = false;
    syncPrimaryAction();
    submitErrorEl.focus();
  }

  /** Leaves the loading state, whatever the attempt resolved or threw. */
  function clearInFlight(): void {
    setInFlight(false);
    statusEl.hidden = true;
    statusEl.textContent = "";
  }

  /**
   * Runs one deliberate submission attempt. The submission controller performs
   * the dedupe, so repeated activation while the call is outstanding resolves to
   * `deduped` and issues no second LeadIntake call. This never rejects: an
   * unexpected exception still releases the loading state and leaves a recoverable
   * step, so the wizard can never be stranded busy with its controls disabled.
   */
  async function submitReferral(): Promise<void> {
    if (!submitter) return;
    const errors = machine.validateCurrent();
    if (errors.length > 0) {
      clearError();
      showError(errors);
      return;
    }

    const wasIdle = !submitter.inFlight;
    if (wasIdle) {
      clearSubmitFeedback();
      statusEl.hidden = false;
      statusEl.textContent = "Submitting your referral…";
      setInFlight(true);
    }

    let outcome: RefinanceSubmissionOutcome;
    try {
      outcome = await submitter.submit(machine.values.snapshot());
    } catch {
      // Nothing about the exception is surfaced or logged; it may carry detail
      // that must not reach the borrower, an embedder, or a console.
      clearInFlight();
      renderUnexpectedFailure();
      return;
    }

    // An observer must not change the outcome, whether it throws or is `async`
    // and rejects.
    contain(() => options.onSubmitOutcome?.(outcome));

    if (outcome.status === "deduped") return;

    clearInFlight();

    if (outcome.status === "success") {
      // The referral is submitted, so a rendering problem must never turn into a
      // retry surface. Completion is recorded only now, so the thank-you panel
      // can never be reached by navigation alone.
      try {
        machine.next();
        renderThankYou(outcome);
      } catch {
        renderMinimalThankYou();
      }
      return;
    }

    try {
      if (outcome.status === "alreadyCompleted") return;
      if (outcome.status === "incomplete") {
        renderIncomplete(outcome.errors);
        return;
      }
      renderSubmitFailure(outcome);
    } catch {
      // Rendering the outcome must never leave the wizard stranded busy.
      clearInFlight();
      renderUnexpectedFailure();
    }
  }

  /**
   * Invokes the documented completion callback best-effort, at most once per
   * wizard, from whichever completion path was reached: the full thank-you, the
   * navigation-only completion, or the minimal acknowledgement fallback. It is
   * contained, so neither a throw nor a rejected promise can affect a referral
   * LeadIntake has already accepted.
   */
  let completeNotified = false;
  function notifyComplete(): void {
    if (completeNotified) return;
    completeNotified = true;
    contain(() => options.onComplete?.(machine.values.snapshot()));
  }

  /**
   * Renders the thank-you experience after a successful LeadIntake response. It
   * replaces the step card rather than adding a numbered step, and only carries
   * non-PII submission metadata (plus debug-only diagnostics). The card it
   * replaces carried the loan-type switch, so an accepted referral offers no
   * way to start over.
   */
  function renderThankYou(
    outcome: Extract<RefinanceSubmissionOutcome, { status: "success" }>,
  ): void {
    const card = el<HTMLDivElement>(".wizard-card");
    const debug = options.submission?.config.debug === true;
    completionEl.className = "wizard-complete";
    completionEl.setAttribute("data-thank-you", "true");
    completionEl.setAttribute("role", "status");
    completionEl.setAttribute("tabindex", "-1");
    completionEl.innerHTML = `
      <h1 class="wizard-step-title">Thank you — your ${escapeHtml(options.referralNoun)} was submitted</h1>
      <p class="wizard-step-desc">
        loanDepot has your referral and accepted it for review. A loanDepot team member will
        follow up with you. Nothing else is needed from you right now.
      </p>
      <dl class="wizard-complete-details">
        <dt>Status</dt><dd>${escapeHtml(outcome.response.status)}</dd>
        <dt>Submitted at</dt><dd>${escapeHtml(outcome.response.submittedAt)}</dd>
      </dl>
      <p class="wizard-correlation">Reference: <span data-correlation>${escapeHtml(outcome.correlationId)}</span></p>
      ${
        debug
          ? `<dl class="wizard-complete-debug">
        ${outcome.response.leadGuid ? `<dt>Lead reference</dt><dd>${escapeHtml(outcome.response.leadGuid)}</dd>` : ""}
        <dt>Message ID</dt><dd>${escapeHtml(outcome.messageId)}</dd>
      </dl>`
          : ""
      }`;
    card.replaceWith(completionEl);
    completionEl.focus();
    notifyComplete();
  }

  /**
   * Last-resort acknowledgement for a submitted referral whose full thank-you
   * panel could not be rendered. It carries no submission metadata at all, but
   * it still tells the borrower the referral is in and never offers a retry for
   * a referral LMD has already accepted.
   */
  function renderMinimalThankYou(): void {
    clearSubmitFeedback();
    const card = root.querySelector<HTMLDivElement>(".wizard-card");
    completionEl.className = "wizard-complete";
    completionEl.setAttribute("data-thank-you", "true");
    completionEl.setAttribute("role", "status");
    completionEl.setAttribute("tabindex", "-1");
    completionEl.innerHTML = `
      <h1 class="wizard-step-title">Thank you — your ${escapeHtml(options.referralNoun)} was submitted</h1>
      <p class="wizard-step-desc">
        loanDepot has your referral and accepted it for review. A loanDepot team member will
        follow up with you. Nothing else is needed from you right now.
      </p>`;
    if (card) card.replaceWith(completionEl);
    else if (!completionEl.isConnected) root.append(completionEl);
    completionEl.focus();
    notifyComplete();
  }

  function renderCompletion(): void {
    const card = el<HTMLDivElement>(".wizard-card");
    completionEl.className = "wizard-complete";
    completionEl.setAttribute("role", "status");
    completionEl.setAttribute("tabindex", "-1");
    completionEl.innerHTML = `
      <h1 class="wizard-step-title">${escapeHtml(options.completion.title)}</h1>
      <p class="wizard-step-desc">${escapeHtml(options.completion.body)}</p>
      <button type="button" class="wizard-btn wizard-btn-secondary" data-restart>Start over</button>`;
    card.replaceWith(completionEl);
    completionEl.querySelector<HTMLButtonElement>("[data-restart]")?.addEventListener(
      "click",
      () => {
        while (!machine.isFirst) machine.back();
        completionEl.replaceWith(card);
        renderStep(true);
      },
    );
    completionEl.focus();
    notifyComplete();
  }

  continueBtn.addEventListener("click", () => {
    if (submitter && machine.isLast) {
      // submitReferral contains its own failures; the catch is the last resort
      // against an unhandled rejection escaping the activation handler.
      void submitReferral().catch(() => clearInFlight());
      return;
    }
    const result = machine.next();
    if (!result.ok) {
      clearError();
      showError(result.errors);
      return;
    }
    renderStep(true);
  });

  // Live validity: every edit and selection inside the step body recomputes
  // whether the current step can continue, so the primary action tracks the
  // step's validity across input, change, and dynamic expansion/collapse. The
  // same edits refresh live guidance for fields already touched, so a malformed
  // entry's explanation appears and clears as the borrower fixes it.
  const onBodyEdit = (): void => {
    // An answer can add or remove a later step, so the numbering the borrower
    // sees is recomputed with every edit rather than only on navigation.
    syncProgress();
    syncPrimaryAction();
    syncLiveGuidance();
  };
  bodyEl.addEventListener("input", onBodyEdit);
  bodyEl.addEventListener("change", onBodyEdit);
  // A control the borrower has entered and left is "touched": from then on its
  // malformed entry is explained inline (announced, associated, focus-neutral)
  // even though Continue stays disabled, so the disabled button is never the
  // sole explanation. `focusout` bubbles, so one delegated listener covers every
  // text-like control the step renders.
  bodyEl.addEventListener("focusout", (event) => {
    if (!isGuidableControl(event.target)) return;
    touched.add(event.target.id);
    syncLiveGuidance();
  });

  /**
   * A step body asked to remove the optional branch it belongs to. A removal is
   * refused outright while an attempt is in flight or once a referral has been
   * accepted — and because the shell owns that decision, a refused removal
   * clears nothing and leaves the borrower exactly where they were rather than
   * emptying answers out from under a request. Otherwise the step's own clear
   * runs and the shell settles the consequences: it prunes what the removal made
   * unreachable, relocates once when the displayed step no longer exists, and
   * re-renders with a clean error/touched/submit-feedback slate. Nothing is
   * validated, skipped, or submitted.
   */
  bodyEl.addEventListener(WIZARD_STEP_RESET_EVENT, (event) => {
    if (machine.isComplete || submitter?.inFlight === true) return;
    (event as CustomEvent<WizardStepResetDetail>).detail.clear();
    const focused = captureBodyFocus();
    machine.pruneInapplicableValues();
    const relocated = machine.reconcileApplicability();
    renderStep(false, relocated);
    // Ordinary removal does not move focus; only a relocation owns it, so the
    // step the borrower was moved to is announced instead of silently swapping
    // the control under their focus.
    if (!relocated) restoreBodyFocus(focused);
  });

  skipBtn.addEventListener("click", () => {
    const step = machine.current;
    if (step.optional !== true || nonRetryableFailure || (submitter !== undefined && machine.isLast)) return;
    // Abandoning an optional answer can make later steps inapplicable — the
    // co-borrower question is exactly that — so the consequences are settled
    // before anything advances. `onSkip` is a clear, so running it here and
    // again inside `skip()` is idempotent.
    step.onSkip?.(machine.values);
    machine.pruneInapplicableValues();
    machine.reconcileApplicability();
    // An optional step that is now the last step is the step the wizard is
    // completed from. Skipping it must never advance into completion, because
    // that would close the wizard out without the deliberate final activation
    // the last step exists for: the borrower stays on it with its optional
    // answers cleared and finishes when they choose to.
    if (machine.isLast) {
      renderStep(false);
      return;
    }
    machine.skip();
    renderStep(true);
  });

  backBtn.addEventListener("click", () => {
    if (machine.isFirst) return;
    machine.back();
    renderStep(true);
  });

  // The switch itself performs no submission, emits no observer event, and
  // advances nothing: it only hands control back to the journey owner, which
  // owns the destructive reset.
  switchBtn?.addEventListener("click", () => {
    if (!canSwitchLoanType()) return;
    options.changeLoanType?.onActivate();
  });

  /**
   * Seeds accepted prefill values into the mounted wizard. They are sanitized
   * exactly like a query seed, so nothing the borrower could not have typed is
   * seeded, and the step is re-rendered with the borrower's own focus and text
   * selection carried onto the replacement control, so someone mid-answer is
   * not interrupted. Prefill never advances a step and never submits, and it is
   * refused outright while an attempt is in flight or once a referral has been
   * submitted, so it can never change what was or is being sent to LMD.
   */
  function applyPrefill(mapped: Readonly<Record<string, string>>): string[] {
    if (machine.isComplete || submitter?.inFlight === true) return [];
    const current = machine.values.snapshot();
    const seeded = dropInapplicableSeeds(sanitizePrefill(mapped, current), current);
    const applied = Object.keys(seeded);
    if (applied.length === 0) return [];
    const focused = captureBodyFocus();
    for (const [key, value] of Object.entries(seeded)) machine.values.set(key, value);
    // A seeded answer supersedes the one the borrower gave before it, so the
    // component it strands is cleared here rather than left held out of sight.
    if (supersedesMilitaryComponent(seeded)) machine.values.set(MILITARY_COMPONENT_KEY, "");
    // An answer that removes a step also removes what that step held, so no
    // stale value survives into the snapshot, validation, or submission.
    machine.pruneInapplicableValues();
    // The sole navigation effect prefill may have: the displayed step no longer
    // exists, so the borrower is relocated once to the next applicable step.
    // Nothing is validated, skipped, or submitted on the way.
    const relocated = machine.reconcileApplicability();
    renderStep(false, relocated);
    // A borrower who was typing keeps their place: the replacement control is
    // focused with the same caret or selection. A relocation deliberately owns
    // focus instead, so the step they were moved to is announced.
    if (!relocated) restoreBodyFocus(focused);
    return applied;
  }

  /** The borrower's focus and text selection inside the step body, if any. */
  interface BodyFocus {
    readonly id: string;
    readonly start: number | null;
    readonly end: number | null;
    readonly direction: "forward" | "backward" | "none" | null;
  }

  /** Selection state, for controls whose type supports one. */
  function selectionOf(el: HTMLElement): Pick<BodyFocus, "start" | "end" | "direction"> {
    if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) {
      return { start: null, end: null, direction: null };
    }
    try {
      // Reading a selection throws on input types that do not support one
      // (email, number); those controls simply restore focus alone.
      return { start: el.selectionStart, end: el.selectionEnd, direction: el.selectionDirection };
    } catch {
      return { start: null, end: null, direction: null };
    }
  }

  /**
   * Records what the borrower is currently editing, so a re-render that
   * replaces the rendered controls can put them back where they were. Only a
   * focused control inside the step body is tracked, and only by its stable id.
   */
  function captureBodyFocus(): BodyFocus | undefined {
    const active = root.ownerDocument.activeElement;
    if (!(active instanceof HTMLElement) || active.id === "" || !bodyEl.contains(active)) {
      return undefined;
    }
    return { id: active.id, ...selectionOf(active) };
  }

  /** Re-focuses the replacement control and restores its caret or selection. */
  function restoreBodyFocus(focused: BodyFocus | undefined): void {
    if (!focused) return;
    const el = bodyEl.querySelector<HTMLElement>(`[id="${focused.id}"]`);
    if (!el) return;
    el.focus();
    if (focused.start === null || focused.end === null) return;
    if (!(el instanceof HTMLInputElement) && !(el instanceof HTMLTextAreaElement)) return;
    try {
      el.setSelectionRange(focused.start, focused.end, focused.direction ?? undefined);
    } catch {
      // A replacement control that does not support a selection keeps focus.
    }
  }

  /**
   * Drops seeded values owned by a step the resulting answers make
   * inapplicable. Applicability is evaluated against the answers the wizard
   * would hold after the seed, so a goal carried by the same message decides
   * admissibility exactly as the borrower's own current answer would. A dropped
   * value is never applied and therefore never acknowledged.
   */
  function dropInapplicableSeeds(
    seeded: Record<string, string>,
    current: Readonly<Record<string, string>>,
  ): Record<string, string> {
    for (const key of inapplicableValueKeys(options.steps, { ...current, ...seeded })) {
      delete seeded[key];
    }
    return seeded;
  }

  renderStep(false);
  options.onPrefillBridge?.(applyPrefill);
  options.onSnapshotBridge?.(() => machine.values.snapshot());
  options.changeLoanType?.onProbe?.(canSwitchLoanType);
  return machine;
}

export { escapeHtml as escapeWizardHtml };
